import { useCallback, useEffect, useMemo, useState, memo, type ReactNode } from "react";

import type { ScaleLinear } from "d3";
import { index } from "d3";
import { Zoom } from "@visx/zoom";
import { useParentSize } from "@visx/responsive";
import { surface } from "@atlas-int/tokens";
import { useDownload } from "@atlas-int/viz";
import {
  XAxisLabels,
  YAxisLabels,
  YAxisOptions,
  AxisAnnotations,
} from "./Axis";
import { GraphLoading } from "@atlas-int/viz/shared";
import { TooltipStateIsolationProvider } from "../../hooks/TooltipStateIsolationProvider";
import { TooltipStateBridge } from "../../components/TooltipStateBridge";
import type { TooltipState, TooltipDatum, TooltipProductDatum } from "../../types/atlasTooltip";
import { color } from "d3-color";
import { moneyFormatter } from "@atlas-int/data";
import {
  TooltipContentsContainer,
  TooltipHeader,
  TooltipBody as SharedTooltipBody,
  TooltipBodyRow,
  TooltipBodyRowLabel,
  TooltipBodyRowValue,
  ProductCodeLabel,
  SectorLabelWithColorIcon,
} from "../../components/tooltip/SharedTooltipComponents";
import ZoomControls from "./ZoomControls";
import ScatterplotBase from "./ScatterplotBase";
import ScatterplotOverlay from "./ScatterplotOverlay";
import { useScatterplotScales } from "./useScatterplotScales";
import { scatterplotOuterStyle, scatterplotInnerStyle } from "./scatterplotStyles";
import type {
  GrowthCPY,
  GrowthPY,
  GrowthCY,
  MetaDataNode,
  MetaDataLookup,
  ProductYearLookup,
  ScatterplotDatum,
  FilteredDataLookup,
  ZoomState,
  ScatterplotProps,
  ScatterplotBaseProps,
  ScatterplotOverlayProps,
  ZoomContentsProps,
} from "./scatterplotTypes";

// Re-export all types so any file that previously imported from Scatterplot
// (or may in the future) can still reach them.
export type {
  GrowthCPY,
  GrowthPY,
  GrowthCY,
  MetaDataNode,
  MetaDataLookup,
  ProductYearLookup,
  ScatterplotDatum,
  FilteredDataLookup,
  ZoomState,
  ScatterplotProps,
  ScatterplotBaseProps,
  ScatterplotOverlayProps,
  ZoomContentsProps,
};

const initialTransform = {
  scaleX: 0.9,
  scaleY: 0.9,
  translateX: 25,
  translateY: 25,
  skewX: 0,
  skewY: 0,
};

const yPadding = 60;

const rescaleYAxis = (
  scale: ScaleLinear<number, number>,
  zoom: ZoomState,
): ScaleLinear<number, number> => {
  let newDomain = scale.range().map((r: number) => {
    return scale.invert(
      (r - zoom.transformMatrix.translateY) / zoom.transformMatrix.scaleY,
    );
  });
  return scale.copy().domain(newDomain);
};
const rescaleXAxis = (
  scale: ScaleLinear<number, number>,
  zoom: ZoomState,
): ScaleLinear<number, number> => {
  let newDomain = scale.range().map((r: number) => {
    return scale.invert(
      (r - zoom.transformMatrix.translateX) / zoom.transformMatrix.scaleX,
    );
  });
  return scale.copy().domain(newDomain);
};

const ZoomContents = ({
  width,
  height,
  yPadding,
  zoom,
  xScale,
  yScale,
  filteredData,
  filteredDataLookup,
  totalExportLookup,
  metaDataLookup,
  ySelection,
  highlightedItem,
  setHighlightedItem,
  productClass,
  countryData,
  svgRef,
}: ZoomContentsProps) => {
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const activeItem = hoveredItem || highlightedItem || null;

  const zoomedXScale = useMemo(
    () => rescaleXAxis(xScale, zoom),
    [xScale, zoom.transformMatrix.translateX, zoom.transformMatrix.scaleX],
  );
  const zoomedYScale = useMemo(
    () => rescaleYAxis(yScale, zoom),
    [yScale, zoom.transformMatrix.translateY, zoom.transformMatrix.scaleY],
  );

  const onDragEnd = useCallback(() => {
    if (zoom.isDragging) {
      zoom.dragEnd();
    }
  }, [zoom]);

  const onSvgRef = useCallback(
    (el: SVGSVGElement | null) => {
      if (el) {
        (zoom.containerRef as React.MutableRefObject<Element | null>).current =
          el;
        svgRef.current = el;
      }
    },
    [svgRef, zoom],
  );

  return (
    <>
      <ZoomControls zoom={zoom} />
      <AxisAnnotations
        activeItem={activeItem}
        filteredDataLookup={filteredDataLookup}
        ySelection={ySelection}
        totalExportLookup={totalExportLookup}
        width={width}
        height={height}
        yPadding={yPadding}
        zoomedXScale={zoomedXScale}
        zoomedYScale={zoomedYScale}
      />
      <svg
        role="img"
        aria-label="Growth opportunities scatterplot"
        style={{ backgroundColor: surface.white }}
        ref={onSvgRef}
        width={width}
        height={height}
      >
        <ScatterplotBase
          width={width}
          height={height}
          yPadding={yPadding}
          zoomedXScale={zoomedXScale}
          zoomedYScale={zoomedYScale}
          zoomTransform={zoom.toString()}
          isDragging={zoom.isDragging}
          onDragStart={zoom.dragStart}
          onDragMove={zoom.dragMove}
          onDragEnd={onDragEnd}
          filteredData={filteredData}
          ySelection={ySelection}
          metaDataLookup={metaDataLookup}
          resetZoom={zoom.reset}
          highlightedItem={highlightedItem}
          setHighlightedItem={setHighlightedItem}
          productClass={productClass}
          totalExportLookup={totalExportLookup}
          setHoveredItem={setHoveredItem}
          countryData={countryData}
        />
        <ScatterplotOverlay
          activeItem={activeItem}
          highlightedItem={highlightedItem}
          filteredDataLookup={filteredDataLookup}
          totalExportLookup={totalExportLookup}
          metaDataLookup={metaDataLookup}
          ySelection={ySelection}
          width={width}
          height={height}
          yPadding={yPadding}
          zoom={zoom}
          zoomedXScale={zoomedXScale}
          zoomedYScale={zoomedYScale}
          zoomTransform={zoom.toString()}
          productClass={productClass}
          setHighlightedItem={setHighlightedItem}
        />
      </svg>
    </>
  );
};

const Scatterplot = ({
  displayData: _displayData,
  countryProductYear,
  productYear,
  loading,
  countryData,
  metaDataLookup,
  metaDataTree: _metaDataTree,
  colorMap,
  hiddenCategories,
  highlightedItem,
  setHighlightedItem,
  productClass,
  sizing,
  hideExports,
  yAxis: ySelection,
  onYAxisChange,
}: ScatterplotProps) => {
  const { svgRef, dataRef } = useDownload();
  const {
    parentRef,
    width,
    height: fullHeight,
  } = useParentSize({ debounceTime: 150 });

  const height = Math.max(0, fullHeight - 20);

  const totalExportLookup: ProductYearLookup = useMemo(() => {
    return index(productYear, (d) => d.productId as string);
  }, [productYear]) as ProductYearLookup;

  const { filteredData, filteredDataLookup, xScale, yScale, rScale: _rScale } =
    useScatterplotScales({
      countryProductYear,
      productYear,
      width,
      height,
      yPadding,
      hideExports,
      ySelection,
      totalExportLookup,
      metaDataLookup,
      sizing,
      hiddenCategories,
      colorMap,
      productClass,
    });

  // Total value and findInVizOptions are now derived in the parent Growth component
  // and provided via VisualizationOutputContext
  useEffect(() => {
    const mappedData = filteredData.map((item) => ({
      Name:
        metaDataLookup.get(item.productId as string)?.data.nameEn || "",
      Code:
        metaDataLookup.get(item.productId as string)?.data.code || "",
      Year: item.year,
      "World Trade":
        totalExportLookup.get(item.productId as string)?.exportValue || 0,
      "Revealed Comparative Advantage (RCA)": item.exportRca,
      Distance: item.distance,
      "Product Complexity Index (PCI)":
        totalExportLookup.get(item.productId as string)?.pci || 0,
      "Opportunity Gain": item.cog,
      Sector:
        metaDataLookup.get(item.topParentId)?.data.nameShortEn || "",
    }));

    dataRef.current = mappedData;
  }, [dataRef, filteredData, metaDataLookup, totalExportLookup]);

  const renderExplanation = useCallback(
    (tooltipState: TooltipState<TooltipDatum> | undefined) => {
      if (!tooltipState?.contentData) return null;
      const datum = tooltipState.contentData;
      if (datum.kind !== "product") return null;
      const d = datum as TooltipProductDatum;
      const sectorName = metaDataLookup.get(
        d.topLevelParent as string,
      )?.data?.nameShortEn;
      const hexColor = color(tooltipState.fillColor)?.formatHex() ?? "#000000";
      return (
        <TooltipContentsContainer>
          <TooltipHeader $backgroundColor={hexColor}>
            <div>
              {d.nameEn} |{" "}
              <ProductCodeLabel>
                {d.productCode} {productClass}
              </ProductCodeLabel>
            </div>
          </TooltipHeader>
          <SharedTooltipBody>
            <TooltipBodyRow>
              <TooltipBodyRowLabel>Sector:</TooltipBodyRowLabel>
              <TooltipBodyRowValue>
                <SectorLabelWithColorIcon $fillColor={hexColor}>
                  {sectorName}
                </SectorLabelWithColorIcon>
              </TooltipBodyRowValue>
            </TooltipBodyRow>
            <TooltipBodyRow>
              <TooltipBodyRowLabel>Distance:</TooltipBodyRowLabel>
              <TooltipBodyRowValue>
                {d.distance ? d.distance.toFixed(3) : "N/A"}
              </TooltipBodyRowValue>
            </TooltipBodyRow>
            {ySelection === "pci" && (
              <TooltipBodyRow>
                <TooltipBodyRowLabel>Product Complexity Index (PCI):</TooltipBodyRowLabel>
                <TooltipBodyRowValue>{d.pci?.toFixed(3)}</TooltipBodyRowValue>
              </TooltipBodyRow>
            )}
            {ySelection === "cog" && (
              <TooltipBodyRow>
                <TooltipBodyRowLabel>Opportunity Gain:</TooltipBodyRowLabel>
                <TooltipBodyRowValue>{undefined}</TooltipBodyRowValue>
              </TooltipBodyRow>
            )}
            <TooltipBodyRow>
              <TooltipBodyRowLabel>Revealed Comparative Advantage (RCA):</TooltipBodyRowLabel>
              <TooltipBodyRowValue>
                {d.exportRca ? d.exportRca.toFixed(3) : "N/A"}
              </TooltipBodyRowValue>
            </TooltipBodyRow>
            <TooltipBodyRow>
              <TooltipBodyRowLabel>World Trade:</TooltipBodyRowLabel>
              <TooltipBodyRowValue>
                {d.exportValue ? moneyFormatter.format(d.exportValue) : "N/A"}
              </TooltipBodyRowValue>
            </TooltipBodyRow>
          </SharedTooltipBody>
        </TooltipContentsContainer>
      );
    },
    [metaDataLookup, productClass, ySelection],
  );

  const clearHighlight = useCallback(() => {
    setHighlightedItem(undefined);
  }, [setHighlightedItem]);

  return (
    <TooltipStateIsolationProvider onHighlight={(id) => setHighlightedItem(id ?? undefined)}>
      <TooltipStateBridge
        renderExplanation={renderExplanation}
        clearHighlightedItem={clearHighlight}
        overrideStyles={true}
      >
        <div style={scatterplotOuterStyle}>
          <YAxisOptions yAxis={ySelection} onYAxisChange={onYAxisChange} />
          <YAxisLabels
            topLabel={ySelection === "pci" ? "More Complex" : "More Opportunity"}
            bottomLabel={
              ySelection === "pci" ? "Less Complex" : "Less Opportunity"
            }
          />
          {loading && <GraphLoading zIndex={100} />}
          <div
            style={scatterplotInnerStyle}
            ref={parentRef}
          >
            <Zoom
              width={width}
              height={height}
              scaleXMin={1 / 2}
              scaleXMax={4}
              scaleYMin={1 / 2}
              scaleYMax={4}
              initialTransformMatrix={initialTransform}
              key={`${ySelection}-${productClass}`}
            >
              {(zoom) => (
                <ZoomContents
                  width={width}
                  height={height}
                  yPadding={yPadding}
                  zoom={zoom}
                  xScale={xScale}
                  yScale={yScale}
                  filteredData={filteredData}
                  filteredDataLookup={filteredDataLookup}
                  totalExportLookup={totalExportLookup}
                  metaDataLookup={metaDataLookup}
                  ySelection={ySelection}
                  highlightedItem={highlightedItem}
                  setHighlightedItem={setHighlightedItem}
                  productClass={productClass}
                  countryData={countryData}
                  svgRef={svgRef}
                />
              )}
            </Zoom>
          </div>
        </div>
        <div>
          <XAxisLabels />
        </div>
      </TooltipStateBridge>
    </TooltipStateIsolationProvider>
  );
};
export default memo(Scatterplot);
